import { useLayoutEffect, useRef, useState } from "react";
import { countApprovalReady, getCount, type CountDetail } from "@/lib/inventory/countsApi";
import type { AuthContextValue } from "@/components/auth/AuthContext";

// Keep literal immutable identifiers; display names are not authorization owners.
export function countOwner(auth: AuthContextValue): string | null {
  const { issuer, sub } = auth.user || {};
  return auth.isReady && auth.isAuthenticated && typeof issuer === "string" && issuer.trim() && typeof sub === "string" && sub.trim()
    ? JSON.stringify([issuer, sub]) : null;
}

// The only displayed detail source: context + monotonic request ownership.
export function useCountRead(id: string, offset: number, observationOffset: number, revision: number) {
  const context = JSON.stringify([id, offset, observationOffset, revision]);
  const request = useRef(0);
  const active = useCountLifetime();
  const committed = useRef<{ context: string; id: string; offset: number; observationOffset: number } | null>(null);
  const [state, setState] = useState<{ context: string; data: CountDetail | null; ready: boolean; error: unknown; busy: boolean }>({ context, data: null, ready: false, error: null, busy: true });
  async function refresh() {
    // Retained mutation callbacks may outlive a revision/page, never their count
    // or the principal-keyed layout lifetime. Ignore uncommitted render arguments.
    const target = committed.current;
    if (!active.current || !target || target.id !== id) return;
    const { context, id: countId, offset, observationOffset } = target;
    const token = ++request.current;
    const current = () => active.current && committed.current === target && token === request.current;
    setState({ context, data: null, ready: false, error: null, busy: true });
    try {
      const data = await getCount(countId, 25, offset, observationOffset);
      if (!current()) return;
      if (data.count_id !== countId) throw new Error("Unexpected count response");
      const ready = await countApprovalReady(data, offset, current).catch(() => false);
      if (!current()) return;
      setState({ context, data, ready, error: null, busy: false });
      return data;
    } catch (error) {
      if (!current()) return;
      setState({ context, data: null, ready: false, error, busy: false });
      throw error;
    }
  }
  useLayoutEffect(() => {
    committed.current = { context, id, offset, observationOffset };
    void refresh().catch(() => {});
    return () => { committed.current = null; ++request.current; };
    // Context contains every immutable request argument; publish only at commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);
  return { ...(state.context === context ? state : { data: null, ready: false, error: null, busy: true }), refresh, requestId: () => request.current };
}

export function useCountLifetime() {
  const active = useRef(false);
  useLayoutEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  return active;
}
